import React, { useEffect, useState } from "react"
import { TextField, Button, Avatar, Typography, Box, Grid, CircularProgress, useTheme } from "@mui/material"
import { useAuthContext } from "../services/auth"
import { Copyright } from "../components/Copyright"
import { useSnackbarContext } from "../services/snackbar"
import { useNavigate, useLocation, Navigate } from "react-router-dom"
import { ReactComponent as HyrisMinimal } from "../assets/svg/hyris-minimal.svg"
import VRlogo from "../assets/png/VR-white.png"
import { CONSTANTS } from "../components/Util"

export const Login = (): React.ReactElement => {
  const navigate = useNavigate()
  const location = useLocation()
  const { open: sbOpen } = useSnackbarContext()
  const { login, isAuth, loading } = useAuthContext()
  const theme = useTheme()
  const [username, setUsername] = useState<string>("")
  const [password, setPassword] = useState<string>("")
  const [failedAttempts, setFailedAttempts] = useState<number>(0)

  const bannerVideoSource = theme.name ? `/api/media/banner/${theme.name}` : "/api/media/banner"

  const rawFrom = location.state?.from ?? { pathname: "/" }
  const redirectTo = typeof rawFrom === "string" ? rawFrom : `${rawFrom.pathname ?? "/"}${rawFrom.search ?? ""}`

  const handleVRlogin = () => {
    const rawFrom = location.state?.from ?? { pathname: "/" }
    const fromPath = typeof rawFrom === "string" ? rawFrom : `${rawFrom.pathname ?? "/"}${rawFrom.search ?? ""}`
    sessionStorage.setItem("VRLoginRedirect", fromPath)
    window.location.href = `/api/login/vr`
  }

  const submit = async () => {
    try {
      await login({ username, password })
      sbOpen("Connexion réussie ! Bienvenue", "success")
      navigate(redirectTo, { replace: true })
    } catch (err: any) {
      setFailedAttempts((prev) => prev + 1)
      sbOpen(err.message, "error")
      console.error(err)
    }
  }

  return isAuth() && !loading ? (
    <Navigate to={redirectTo} replace />
  ) : (
    <Grid
      container
      component="main"
      sx={{
        height: "100vh",
        overflow: "hidden",
      }}
    >
      <Grid
        size={7}
        sx={{
          display: { xs: "none", md: "flex" },
          alignItems: "center",
          justifyContent: "center",
          overflowX: theme.name === "glass" ? "visible" : "hidden",
          overflowY: "hidden",
          maxHeight: "100vh",
        }}
      >
        <Box
          component="video"
          autoPlay
          loop
          muted
          sx={{
            objectFit: "cover",
            height: "100vh",
            ...(theme.name === "glass"
              ? {
                  position: "absolute",
                  minWidth: "100vw",
                  top: 0,
                  left: 0,
                }
              : {}),
          }}
        >
          <source src={bannerVideoSource} type="video/mp4" />
        </Box>
      </Grid>

      <Grid
        size={{ xs: 12, md: 5 }}
        sx={{
          height: "100%",
          overflow: "scroll",
          boxShadow: "0px 0px 5px #000",
          backgroundColor: theme.palette.element.login?.background ?? theme.palette.primary.main,
          backdropFilter: theme.name === "glass" ? "blur(5px)" : "unset",
        }}
      >
        <Box sx={{ position: "relative", zIndex: "300", marginBottom: 2 }}>
          <Box
            sx={{
              mx: 4,
              my: 8,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Avatar
              sx={{
                margin: 1,
                backgroundColor: "transparent",
              }}
            >
              <HyrisMinimal width="30" height="30" fill="white" />
            </Avatar>
            <Typography component="h1" variant="h5">
              {"Connexion"}
            </Typography>
            <Button
              type="submit"
              color="secondary"
              variant="contained"
              onClick={handleVRlogin}
              sx={{
                p: 4,
                my: 6,
                mx: 0,
                width: `${300 + Math.max(0, failedAttempts - 1) * 30}px`,
                height: `${150 + Math.max(0, failedAttempts - 1) * 15}px`,
                transition: ".2s",
                position: "relative",
                overflow: "hidden",
                bgcolor: "#4e5fc4",
                "&:hover": {
                  bgcolor: "#35459dff",
                },
                "&::before": {
                  content: '""',
                  position: "absolute",
                  inset: 0,
                  backgroundImage: `url(${VRlogo})`,
                  backgroundRepeat: "no-repeat",
                  backgroundPosition: `left -${30 + Math.max(0, failedAttempts - 1) * 3}px top -${
                    20 + Math.max(0, failedAttempts - 1) * 2
                  }px`,
                  backgroundSize: `${250 + Math.max(0, failedAttempts - 1) * 25}px`,
                  transition: ".2s",
                  opacity: 0.3,
                  pointerEvents: "none",
                  zIndex: 0,
                },
              }}
            >
              <Typography
                component="span"
                textTransform="none"
                fontWeight={"bold"}
                fontSize={`${1.5 + Math.max(0, failedAttempts - 1) * 0.1}rem`}
                sx={{ transition: ".2s", position: "relative", zIndex: 1, color: "#fff" }}
              >
                {"Connexion avec ViaRézo"}
              </Typography>
            </Button>
            <Box mt={4} width={"100%"}>
              <form
                onSubmit={(e) => {
                  e.preventDefault()
                  submit()
                }}
                style={{ width: "100%", display: "flex", flexDirection: "column", alignItems: "start" }}
              >
                <Typography component="h2" variant="h5" fontSize="1.2rem" alignSelf="start">
                  {"Pour les membres d'Hyris :"}
                </Typography>
                <TextField
                  variant="filled"
                  margin="normal"
                  color="secondary"
                  fullWidth
                  label="Identifiant"
                  name="username"
                  autoComplete="username"
                  disabled={loading}
                  value={username}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setUsername(e.currentTarget.value)}
                />
                <TextField
                  variant="filled"
                  margin="normal"
                  color="secondary"
                  fullWidth
                  label="Mot de passe"
                  name="password"
                  autoComplete="current-password"
                  type="password"
                  disabled={loading}
                  value={password}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.currentTarget.value)}
                />
                <Box
                  sx={{
                    my: 2,
                  }}
                >
                  <Button
                    type="submit"
                    color="secondary"
                    variant="contained"
                    disabled={loading}
                    sx={{ marginRight: 2 }}
                  >
                    <Box
                      sx={{
                        visibility: loading ? "hidden" : "visible",
                        width: "100%",
                        display: "inline-block",
                        textAlign: "center",
                      }}
                    >
                      {"Valider"}
                    </Box>
                    {loading && (
                      <Box
                        sx={{
                          position: "absolute",
                          inset: 0,
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                      >
                        <CircularProgress
                          size={24}
                          sx={{
                            color: "#fff",
                          }}
                        />
                      </Box>
                    )}
                  </Button>
                  <Button
                    color="error"
                    variant="outlined"
                    sx={{
                      mt: 2,
                      mb: 2,
                      mx: 0,
                    }}
                    onClick={() => navigate("/")}
                  >
                    {"Retour"}
                  </Button>
                </Box>
              </form>
            </Box>
          </Box>
          <Box mt={8}>
            <Copyright />
            <Typography variant="subtitle1" align="center" color="textSecondary" component="p">
              {`v${CONSTANTS.version}`}
            </Typography>
          </Box>
        </Box>
      </Grid>
    </Grid>
  )
}

export const VRCallback = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { open: sbOpen } = useSnackbarContext()
  const { VRlogin } = useAuthContext()

  useEffect(() => {
    const fetchToken = async () => {
      try {
        await VRlogin(location.search)
        sbOpen("Connexion réussie ! Bienvenue", "success")
      } catch (err: any) {
        sbOpen("Erreur de connexion...", "error")
      } finally {
        const redirectAfterVR = sessionStorage.getItem("VRLoginRedirect") || "/"
        sessionStorage.removeItem("VRLoginRedirect")
        navigate(redirectAfterVR, { replace: true })
      }
    }

    fetchToken()
  }, [location.search])

  return <p>{"Loading..."}</p>
}
